import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { theme } from '../../../theme';

const { Option } = Select;

interface ObjetoModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  objeto?: {
    id: number;
    descripcion: string;
    clasificacion: number;
  } | null;
}

const ObjetoModal: React.FC<ObjetoModalProps> = ({ visible, onClose, onSuccess, objeto }) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const empresa = user?.id_empresa;
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);

  const isEditing = !!objeto;

  // Cargar datos si es edición
  useEffect(() => {
    if (visible && objeto) {
      form.setFieldsValue({
        descripcion: objeto.descripcion,
        clasificacion: objeto.clasificacion
      });
    } else {
      form.resetFields();
    }
  }, [visible, objeto, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const accessToken = await getAccessTokenSilently();
      const url = isEditing
        ? `${process.env.REACT_APP_INSUMOS_URL}/editar_objetos/${empresa}/${objeto!.id}`
        : `${process.env.REACT_APP_INSUMOS_URL}/crear_objetos`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(isEditing ? values : {
          ...values,
          id_empresa: empresa,
          tipo: values.clasificacion
        }),
      });

      if (!response.ok) {
        throw new Error('Error en la respuesta del servidor');
      }

      message.success(`Objeto ${isEditing ? 'actualizado' : 'creado'} correctamente`);
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      message.error(`Error al ${isEditing ? 'actualizar' : 'crear'} el objeto`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span style={{ 
          color: theme.colors.primaryDark, 
          fontSize: theme.fontSizes.large,
          fontWeight: 600
        }}>
          {isEditing ? 'Editar Objeto' : 'Nuevo Objeto'}
        </span>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText={isEditing ? 'Actualizar' : 'Crear'}
      cancelText="Cancelar"
      okButtonProps={{
        style: {
          backgroundColor: theme.colors.secondary,
          borderColor: theme.colors.secondary,
          fontWeight: 500
        }
      }}
      cancelButtonProps={{
        style: {
          fontWeight: 500
        }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: '20px' }}
        initialValues={{
          clasificacion: 1 // Grupo por defecto
        }}
      >
        <Form.Item 
          name="descripcion" 
          label={
            <span style={{ 
              color: theme.colors.textPrimary, 
              fontWeight: 600 
            }}>
              Descripción
            </span>
          }
          rules={[{ required: true, message: 'Por favor ingrese una descripción' }]}
        >
          <Input 
            maxLength={100} 
            placeholder="Ingrese la descripción del objeto"
          />
        </Form.Item>

        <Form.Item 
          name="clasificacion" 
          label={
            <span style={{ 
              color: theme.colors.textPrimary, 
              fontWeight: 600 
            }}>
              Tipo
            </span>
          }
          rules={[{ required: true, message: 'Por favor seleccione un tipo' }]}
        >
          <Select placeholder="Seleccione un tipo">
            <Option value={1}>Grupo</Option>
            <Option value={2}>Subgrupo</Option>
            <Option value={3}>Marca</Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ObjetoModal;

